/**
 * A governed act's input, as fields the shared Form can render (#1131).
 *
 * ── why this is a pure function in its own file ────────────────────────────
 * Ruling 40821 point 3 admits a generated form only for a "bounded, simple
 * input schema", and `core.tool_schema_is_form_safe` is what decides that — in
 * SQL, at registration, so an unrenderable schema is refused before anything
 * reaches a screen. This is the client half of the same contract, and it must
 * agree with it exactly. A mapping buried inside a component is one nobody can
 * put a schema into and read the answer out of, which is how the two halves
 * drift.
 *
 * ── the identity argument is REMOVED, not disabled ─────────────────────────
 * The ruling says the form renders "with record identity prefilled and
 * immutable". A disabled field showing a uuid is neither: it is furniture that
 * invites a question, and on a batch action there is no single id to show.
 * The surface supplies the identity when it invokes; the person never sees it.
 */

/** The subset of JSON Schema a form-safe tool input can use. */
interface PropSpec {
  type?: string
  title?: string
  description?: string
  enum?: unknown[]
}

export interface ActionFormField {
  name: string
  type: 'text' | 'number' | 'switch' | 'select'
  label: string
  required: boolean
  options?: { value: string; label: string }[]
  hint?: string
}

/** `p_cost_centre` → `Cost centre`. Only used when the schema names nothing better. */
export function humanise(name: string): string {
  const words = name.replace(/^p_/, '').replace(/[_-]+/g, ' ').trim()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : name
}

/**
 * The fields a person must fill to take this act.
 *
 * `exclude` carries the identity arguments. Returns `[]` for an act that needs
 * nothing beyond the record — which is the common case and the reason the
 * caller can tell "no form needed" from "a form with no fields".
 */
export function actionFormFields(
  schema: Record<string, unknown> | null | undefined,
  exclude: (string | null)[] = [],
): ActionFormField[] {
  const props = (schema?.properties ?? {}) as Record<string, PropSpec>
  const required = new Set(Array.isArray(schema?.required) ? (schema.required as string[]) : [])
  const skip = new Set(exclude.filter((x): x is string => !!x))

  return Object.entries(props)
    .filter(([name]) => !skip.has(name))
    .map(([name, spec]) => {
      const label = spec.title?.trim() || humanise(name)
      const hint = spec.description?.trim() || undefined
      if (Array.isArray(spec.enum) && spec.enum.length > 0) {
        return {
          name,
          type: 'select' as const,
          label,
          required: required.has(name),
          hint,
          options: spec.enum.map((v) => ({ value: String(v), label: humanise(String(v)) })),
        }
      }
      /* The registrar admits string | number | integer | boolean and nothing
         else, so an unknown type here means the two halves have drifted. Render
         it as text rather than dropping it: a field the person cannot see is a
         value the verb will refuse for, with nothing on screen to explain it. */
      const type = spec.type === 'boolean' ? 'switch'
        : spec.type === 'number' || spec.type === 'integer' ? 'number'
          : 'text'
      return { name, type, label, required: required.has(name), hint }
    })
}
